import { INotification } from '../../../data/entities/notification';
import { IAction } from './types';

export enum NotificationTypes {
	PRICING_INCOMPLETE = 'pricing_incomplete',
	MISSING_FINANCIAL = 'missing_financial_details',
	FIRST_LIVE_LISTING = 'listing_is_live_on_channel'
}

const isKnownType = (nType: string): nType is NotificationTypes => Object.values(NotificationTypes).includes(nType as NotificationTypes);

const type = (notification: INotification): IAction['classification'] => {
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			return 'mandatory';
		case NotificationTypes.MISSING_FINANCIAL:
			return 'mandatory';
		case NotificationTypes.FIRST_LIVE_LISTING:
			return 'optional';
		default:
			return 'optional';
	}
};

const title = (notification: INotification): IAction['title'] => {
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			return 'Complete your pricing';
		case NotificationTypes.MISSING_FINANCIAL:
			return 'Add your financial details';
		case NotificationTypes.FIRST_LIVE_LISTING:
			return 'View your live listing';
		default:
			return '';
	}
};

const text = (notification: INotification): IAction['text'] => {
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			const missing = notification.content && notification.content.issues &&
				notification.content.issues.hasOwnProperty('any_pricing') && notification.content.issues.any_pricing === false;
			if (missing) {
				return 'Your property has no pricing. You will be unable to receive bookings if you have no pricing.';
			}
			return 'Your property has incomplete pricing, which may lead to fewer bookings.';
		case NotificationTypes.MISSING_FINANCIAL:
			return 'Add your financial details and start receiving payments straight away.';
		case NotificationTypes.FIRST_LIVE_LISTING:
			const info = notification.content && notification.content.context;
			const channel = info && notification.content.context.channel;
			const property = info && notification.content.context.property;
			const channelName = channel && notification.content.context.channel.name ? notification.content.context.channel.name : '';
			const channelUrl = channel && notification.content.context.channel.url ? notification.content.context.channel.url : '';
			const propertyName = property && notification.content.context.property.name ? notification.content.context.property.name : 'Property';

			return [
				`${propertyName} is now live${channelName && channelUrl ? ` on ${channelName}: ${channelUrl}` : ''}.`,
				'Please snooze or deactivate any current listings on this channel to avoid double bookings.'
			];
		default:
			return '';
	}
};

const context = (notification: INotification): IAction['context'] => {
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			return ((notification.content.context || {}).property || {}).name || '';
		case NotificationTypes.FIRST_LIVE_LISTING:
			return ((notification.content.context || {}).property || {}).name || '';
		default:
			return '';
	}
};

const link = (notification: INotification): IAction['link'] => {
	let id = '';
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			id = ((notification.content.context || {}).property || {}).id || '';
			return id ? `/property/${id}/edit/tasks/pricing` : '';
		case NotificationTypes.MISSING_FINANCIAL:
			return '/account/business';
		case NotificationTypes.FIRST_LIVE_LISTING:
			id = ((notification.content.context || {}).property || {}).id || '';
			return id ? `/property/${id}/listings` : '';
		default:
			return '';
	}
};

const linkLabel = (notification: INotification): IAction['linkLabel'] => {
	switch (notification.notification_type) {
		case NotificationTypes.PRICING_INCOMPLETE:
			return 'Update pricing';
		case NotificationTypes.MISSING_FINANCIAL:
			return 'Add financial details';
		case NotificationTypes.FIRST_LIVE_LISTING:
			return 'View live listing';
		default:
			return '';
	}
};

const priority = (notification: INotification): IAction['priority'] => {
	switch (notification.notification_type) {
		case NotificationTypes.MISSING_FINANCIAL:
			return 1;
		default:
			return 10;
	}
};

const dismissable = (notification: INotification): boolean => {
	switch (notification.notification_type) {
		case NotificationTypes.MISSING_FINANCIAL:
			return false;
		default:
			return true;
	}
};

export const isDeferred = (notification: INotification): boolean => {
	if (!notification.deferred_until) {
		return false;
	}

	const deferralDate = new Date(notification.deferred_until);

	return deferralDate > new Date();
};

export const actionFromNotification = (
	notification: INotification,
	dismissNotification?: (id: INotification['id']) => any
): IAction | null => {
	const { content = {}, id, notification_type } = notification;

	if (!isKnownType(notification_type)) {
		return null;
	}

	if (isDeferred(notification)) {
		return null;
	}

	const action: IAction = {
		type: notification_type,
		classification: content.type || type(notification),
		title: content.title || title(notification),
		text: content.text || text(notification),
		context: context(notification),
		link: content.link || link(notification),
		linkLabel: content.linkLabel || linkLabel(notification),
		priority: content.priority || priority(notification),
		consumer: { id: notification.recipient, type: 'customer' }
	};

	if (dismissNotification && id && dismissable(notification)) {
		action.dismiss = () => dismissNotification(id);
	}

	return action;
};

export const actionsFromNotifications = (
	notifications: INotification[],
	dismissNotification?: (id: INotification['id']) => any
): IAction[] => {
	return notifications.map((n) => actionFromNotification(n, dismissNotification)).filter((a): a is IAction => !!a);
};

export const sortByPriority = (actions: IAction[]) => [...actions].sort((a, b) => (a.priority || 99) - (b.priority || 99));
